// PaymentRule (AIAgent_PaymentRules) sub-entity client — restores the "Payment Rules" Agent tab
// dropped in the 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BasePaymentRuleController), verified by reading the controller
// and entity directly.
//
// Route confirmed as briefed: `[Route("api/v1/ai-agent/payment-rules")]` on
// BasePaymentRuleController.cs — `/payment-rules`, not `/agent-payment-rules`.
//
// Entity confirmed as briefed: PaymentRule.cs is a blockchain payment-validation record
// (PaymentAccountID/Amount/Currency/MaxSpendLimit/IsValidateSenderSignature/
// IsValidateRecipientSignature/Attribute1-9), NOT a percentage-based rule. Additional fields the
// briefing didn't mention: optional EventID/RequestID/ConversationID/AgentRunID int FKs and an
// optional `Hash` string(200) column. Documented in DevelopmentHistoryLog.md.
//
// CONFIRMED BACKEND GAP (re-confirmed this pass, still true): BasePaymentRuleController.cs has no
// by-agent/by-agent-id query route at all — only `list` (generic GetAll), `by-type`, and `active`.
// Every sibling sub-entity controller in this file set (Capability/AgentQuota/Policy/
// AgentDataSample/AgentScope/Version) has a by-agent(-id) route; this one alone does not.
// Workaround: getPaymentRulesByAgent calls the generic `list` endpoint (GoWebStandardListRequest,
// same PageInfo shape as every other list/search call here — GoWebStandardListRequest and
// GoWebStandardSearchRequest share the same GoWebBaseSearchRequest base, confirmed by reading
// StandardListRequest.cs) and filters the page client-side by AgentID.
//
// FAST-FOLLOW CANDIDATE: if the AIAgent_PaymentRules table grows large in production, this
// client-side filter over a single unfiltered page (capped at 1000 rows below) will silently miss
// rows past that cap for agents with many payment rules across the tenant. The clean fix is a
// dedicated `POST /api/v1/ai-agent/payment-rules/by-agent` route on the backend, mirroring every
// sibling controller's shape — flagged here for whoever owns BasePaymentRuleController.cs next,
// not built in this pass (backend changes are out of scope for this frontend-restoration task).

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { PaymentRuleDTO, PaymentRuleFormValues } from '../types';

export class AgentPaymentRuleAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  /** Workaround for the confirmed missing by-agent route — see this file's header comment. */
  async getPaymentRulesByAgent(agentId: number): Promise<AdminApiResult<PaymentRuleDTO[]>> {
    const result = await this.postQuery<PaymentRuleDTO[]>('/api/v1/ai-agent/payment-rules/list', {
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    if (!result.success || !Array.isArray(result.data)) {
      return { ...result, data: [] };
    }
    return { ...result, data: result.data.filter((rule) => rule.AgentID === agentId) };
  }

  async createPaymentRule(agentId: number, rule: PaymentRuleFormValues): Promise<AdminApiResult<PaymentRuleDTO>> {
    return this.post<PaymentRuleDTO>('/api/v1/ai-agent/payment-rules', { AgentID: agentId, ...rule });
  }

  async updatePaymentRule(paymentRuleId: number, rule: Partial<PaymentRuleFormValues>): Promise<AdminApiResult<PaymentRuleDTO>> {
    return this.put<PaymentRuleDTO>(`/api/v1/ai-agent/payment-rules/${paymentRuleId}`, {
      PaymentRuleID: paymentRuleId,
      ...rule,
    });
  }

  async deletePaymentRule(paymentRuleId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/payment-rules/${paymentRuleId}`, { id: paymentRuleId });
  }
}
